<template lang="pug">
  .rma-select-items(ref="root")
    h3 {{ __('Order') }} {{ order.incrementId || '' }}
    rma-skeleton(v-if="!order.items")
    template(v-else)
      section
        Row
          Column(width="4")
            RogueInput.form-field(
              label="Name"
              placeholder="Ex: John Doe"
              required
              v-model="formData.name"
            )
          Column(width="4")
            RogueInput.form-field.field-phone(
              label="Phone"
              placeholder="Ex: 614-123-4567"
              type="tel"
              required
              v-model="formData.phone"
            )
        p.font-size-xxs.required {{ __('* Required Fields') }}
      section.reprint-rma(v-if="hasPreviousRMA")
        h4 {{ __('Reprint a previous RMA') }}
        Row
          Column(width="4")
            RogueSelect.form-field(
              label="Select an RMA"
              :options="getIssuedRMA"
              v-model="orderToPrint"
            )
          Column(width="4")
            RogueButton.form-field(
              :text="__('Print RMA')"
              width="full"
              color="black"
              @click="printSelectedRMA"
            )
      section
        .title.font-weight-bold {{ __('Select items for return or exchange') }}
        .product(
          v-for="product in order.items"
          :key="product.id"
        )
          Checkbox(
            :modelValue="getProductCheckboxValue(product.number) || product.qtyReturnable === 0"
            @update:modelValue="setReturnProduct(product.number, $event)"
            :disabled="product.qtyReturnable === 0"
            :id="`checkbox-'${product.id}`"
          )
            rma-product(:product="product")
          returning-details(
            :product="order.items.find(p => p.number === product.number) || product"
            :selected-items="formData.items"
            v-if="getProductCheckboxValue(product.number)"
            @input="setReturnProductDetails(product.number, $event)"
          )
        p.font-size-xxs.field-error(v-if="showEmptyItemsError")
          | {{ __('Please, select the items you wish to return.') }}
      section
        RogueTextarea(
          label="Return Comments (Optional)"
          v-model="formData.notes"
          :placeholder="__('Enter multi-line plain text here.')"
        )
        p.info(v-translate)
          | If you received an item that does not match what you ordered please &nbsp;
          a(@click="$router.push('/contact-us')") contact us
          | .
        Row
          Column(width="4")
            RogueButton(
              :text="__('Continue')"
              color="black"
              width="full"
              :disabled="!formData.items.length"
              @click="proceedToConfirm"
            )
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ReturningDetails    from '../components/rma-product-returning-details.vue'
import RmaProduct          from '../components/rma-product.vue'
import RmaSkeleton from '../components/rma-skeleton.vue'
import { formatDate }     from '../helpers'
import type { ReturnProductDetails, RMAOrder, FormData } from '../types'
import { validate } from '@/js/helpers/validate-fns'

const root = ref<HTMLDivElement | null>(null)
const router = useRouter()
const route = useRoute()

const props = withDefaults(defineProps<{
  order: RMAOrder,
  formData: FormData,
  processing?: boolean,
}>(), {
  processing: false,
})

const showEmptyItemsError = ref<boolean>(false)
const orderToPrint = ref<string | null>(null)
  
const hasPreviousRMA = computed<boolean>(() => !!props.order?.rmas?.length)
const getIssuedRMA = computed(() => props.order?.rmas
  ?.slice() // avoid mutating the original array
  .sort((a, b) => new Date(a.dateIssued).getTime() - new Date(b.dateIssued).getTime()) // descending
  .map(r => ({
    label : `${formatDate(r.dateIssued)} - ${r.number}`,
    value : r.number,
  }))) 
  
function checkOrderAndRedirect() {
  if (!props.order || !Object.keys(props.order).length) {
    router.replace({ path: '/rmaprocess/initiate', query: route.query })
  }
  if (props.order.rmas?.length) {
    orderToPrint.value = props.order.rmas[0].number
  }
}

onMounted(() => {
  if (!props.processing) {
    checkOrderAndRedirect()
  }
})

watch(() => props.processing, (processing) => {
  if (!processing) {
    checkOrderAndRedirect()
  }
})
   

function proceedToConfirm() {
  showEmptyItemsError.value = Boolean(!props.order.items?.length)

  if (!validate(root.value!) || showEmptyItemsError.value) {
    return
  }

  if (props.formData.name && props.formData.phone && props.order.items?.length) {
    router.push({ path: '/rmaprocess/confirm', query: route.query })
  }
}

function getProductCheckboxValue(number:number) {
  return Boolean(props.formData?.items?.find(i => i.number === number))
}

function setReturnProduct(itemNumber:number, selected:boolean) {
  if (selected) {
    const { sku, number } = props.order.items.find((p) => p.number === itemNumber)!
    props.formData.items.push({ sku, number })
  } else {
    props.formData.items = props.formData.items.filter((p) => p.number !== itemNumber)
  }
}

function setReturnProductDetails(itemNumber:number, details:ReturnProductDetails) {
  const idx = props.formData.items.findIndex((i) => i.number === itemNumber)
  const item = { ...props.formData.items[idx], ...details }
  props.formData.items = [].concat(props.formData.items.filter((i) => i.number !== itemNumber), [item])
}

async function printSelectedRMA() {
  if (!orderToPrint.value) {
    return
  }

  const rma = props.order?.rmas?.find((r) => r.number === orderToPrint.value)
  router.push({
    name   : 'rmaPrintReturn',
    query  : route.query,
    params : { rmaNumber: rma?.number, dateIssued: rma?.dateIssued?.toString() },
  })
}

</script>

<style lang="scss" scoped>
.product {
  .checkbox {
    align-items: flex-start;

    &.disabled {
      cursor: not-allowed;

      :deep(.box) {
        border-color: #989c9f;
        background: #efefef;

        .icon svg {
          fill: $color-gray-dark !important; // needed
        }
      }
    }
  }
}
.info {
  margin: $space-m 0;
  line-height: $line-height-m;
  font-size: $size-s;
}
.return-details {
  margin-top: $space-s;
  margin-left: 26px;
}
.reprint-rma {
  .button.form-field {
    margin-bottom: 0;
  }
}
.field-phone {
  margin-bottom: $space-xxs;
}

@include not-mobile {
  .reprint-rma .row:last-child .field {
    margin-bottom: 0;
  }
}
</style>
